/* eslint-disable prefer-const */
import { inject, Injectable } from "@angular/core";
import { map } from "rxjs";
import { firstValueFrom } from "rxjs/internal/firstValueFrom";
import { HttpService } from "src/app/session/services/http.service";
import { environment } from "src/environments/environment";

import {
  TimesheetActivity,
  TimesheetActivityFilter,
  TimesheetActivityUpdateModel,
} from "../model/timesheet-activity";

@Injectable({
  providedIn: "root",
})
export class TimesheetActivitiesService {
  private readonly httpService = inject(HttpService);

  public getTimesheetActivities(
    timesheetActivityFilter: TimesheetActivityFilter,
  ): Promise<TimesheetActivity[]> {
    return firstValueFrom(
      this.httpService
        .get(
          `${environment.webApiUrl}/api/TimeManagement/inspectors/activities?currentPage=${timesheetActivityFilter.currentPage}&pageSize=${timesheetActivityFilter.pageSize}&dateFrom=${timesheetActivityFilter.startDate ? this.getDate(timesheetActivityFilter.startDate) : ''}&dateTo=${timesheetActivityFilter.endDate ? this.getDate(timesheetActivityFilter.endDate) : ''}&externalId=${timesheetActivityFilter.inspectionId}&userEmail=${timesheetActivityFilter.inspectorEmail ?? ""}&teamId=${timesheetActivityFilter.teamId}&activityId=${timesheetActivityFilter.activity}&timesheetActivityStatus=${timesheetActivityFilter.status}&contractLocalNumber=${timesheetActivityFilter.inspectionContractLocalNumber}&sortAsc=${timesheetActivityFilter.sortAsc}`,
          {
            withCredentials: true,
          },
        )
        .pipe(
          map((response: TimesheetActivity[]) => {
            return response.map((timesheetActivity: TimesheetActivity) => ({
              ...timesheetActivity,
              dayShiftHours: this.parseTimeToHoursFormat(
                timesheetActivity.dayShiftHours,
              ),
              overtimeHours: this.parseTimeToHoursFormat(
                timesheetActivity.overtimeHours,
              ),
              nightShiftHours: this.parseTimeToHoursFormat(
                timesheetActivity.nightShiftHours,
              ),
              doubleShiftHours: this.parseTimeToHoursFormat(
                timesheetActivity.doubleShiftHours,
              ),
              status: timesheetActivity.status.toString(),
              isValid: true,
            }));
          }),
        ),
    );
  }

  public getInspectorTimesheetActivities(
    inspectionId: string,
  ): Promise<TimesheetActivity[]> {
    return firstValueFrom(
      this.httpService
        .get(
          `${environment.webApiUrl}/api/TimeManagement/inspector/activities?inspectionId=${inspectionId}`,
          {
            withCredentials: true,
          },
        )
        .pipe(
          map((response: TimesheetActivity[]) => {
            return response.map((timesheetActivity: TimesheetActivity) => ({
              ...timesheetActivity,
              dayShiftHours: this.parseTimeToHoursFormat(
                timesheetActivity.dayShiftHours,
              ),
              overtimeHours: this.parseTimeToHoursFormat(
                timesheetActivity.overtimeHours,
              ),
              nightShiftHours: this.parseTimeToHoursFormat(
                timesheetActivity.nightShiftHours,
              ),
              doubleShiftHours: this.parseTimeToHoursFormat(
                timesheetActivity.doubleShiftHours,
              ),
              status: timesheetActivity.status.toString(),
              isValid: true,
            }));
          }),
        ),
    );
  }

  private getDate(date: Date): string | null {
    if (!date) return null;
    const utcFromDate: Date = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    );
    return utcFromDate.toISOString().split("T")[0];
  }

  private parseTimeToHoursFormat(timeValue: string): string {
    if (!timeValue) return "";

    let [hours, minutes] = timeValue.split(":").slice(0, 2);

    return `${hours}H ${minutes}m`;
  }

  public saveTimesheetActivity(
    timesheetActivity: TimesheetActivity,
  ): Promise<string> {
    timesheetActivity = {
      ...timesheetActivity,
      date: this.getDate(new Date(timesheetActivity.date)),
      dayShiftHours: this.parseHoursToTimeFormat(
        timesheetActivity.dayShiftHours,
      ),
      overtimeHours: this.parseHoursToTimeFormat(
        timesheetActivity.overtimeHours,
      ),
      nightShiftHours: this.parseHoursToTimeFormat(
        timesheetActivity.nightShiftHours,
      ),
      doubleShiftHours: this.parseHoursToTimeFormat(
        timesheetActivity.doubleShiftHours,
      ),
    };

    return firstValueFrom(
      this.httpService.post(
        `${environment.webApiUrl}/api/TimeManagement/inspector/activity`,
        timesheetActivity,
        {
          withCredentials: true,
          responseType: "text",
        },
      ),
    );
  }

  public updateTimesheetActivity(
    timesheetActivity: TimesheetActivity,
  ): Promise<string> {
    const timesheetActivityUpdateModel: TimesheetActivityUpdateModel = {
      entryId: timesheetActivity.id,
      ...timesheetActivity,
      date: this.getDate(new Date(timesheetActivity.date)),
      dayShiftHours: this.parseHoursToTimeFormat(
        timesheetActivity.dayShiftHours,
      ),
      overtimeHours: this.parseHoursToTimeFormat(
        timesheetActivity.overtimeHours,
      ),
      nightShiftHours: this.parseHoursToTimeFormat(
        timesheetActivity.nightShiftHours,
      ),
      doubleShiftHours: this.parseHoursToTimeFormat(
        timesheetActivity.doubleShiftHours,
      ),
    };

    return firstValueFrom(
      this.httpService.put(
        `${environment.webApiUrl}/api/TimeManagement/inspector/activity`,
        timesheetActivityUpdateModel,
        {
          withCredentials: true,
        },
      ),
    );
  }

  private parseHoursToTimeFormat(hourValue: string): string {
    if (!hourValue) return "00:00:00";

    let hours = hourValue.split("H")[0].trim();
    if (hours.length !== 2) hours = "0" + hours;

    let minutes = hourValue.split("m")[0].split("H")[1]?.trim() || "0";
    if (minutes.length !== 2) minutes = "0" + minutes;

    return `${hours}:${minutes}:00`;
  }

  public deleteTimesheetActivity(timesheetActivityId: string): Promise<string> {
    return firstValueFrom(
      this.httpService.delete(
        `${environment.webApiUrl}/api/TimeManagement/inspector/activity/${timesheetActivityId}`,
        {
          withCredentials: true,
          responseType: "text",
        },
      ),
    );
  }

  public submitTimesheetActivities(inspectionId: string): Promise<void> {
    return firstValueFrom(
      this.httpService.put(
        `${environment.webApiUrl}/api/TimeManagement/inspection/${inspectionId}/sumbitActivities`,
        {
          withCredentials: true,
        },
      ),
    );
  }

  public saveTimesheetActivityFromInspectorRole(
    timesheetActivity: TimesheetActivity,
    inspectionId: string,
  ): Promise<string> {
    timesheetActivity = {
      ...timesheetActivity,
      date: this.getDate(new Date(timesheetActivity.date)),
      dayShiftHours: this.parseHoursToTimeFormat(
        timesheetActivity.dayShiftHours,
      ),
      overtimeHours: this.parseHoursToTimeFormat(
        timesheetActivity.overtimeHours,
      ),
      nightShiftHours: this.parseHoursToTimeFormat(
        timesheetActivity.nightShiftHours,
      ),
      doubleShiftHours: this.parseHoursToTimeFormat(
        timesheetActivity.doubleShiftHours,
      ),
    };

    return firstValueFrom(
      this.httpService.post(
        `${environment.webApiUrl}/api/TimeManagement/inspection/${inspectionId}/activity`,
        timesheetActivity,
        {
          withCredentials: true,
          responseType: "text",
        },
      ),
    );
  }

  public updateTimesheetActivityFromInspectorRole(
    timesheetActivity: TimesheetActivity,
    inspectionId: string,
  ): Promise<string> {
    const timesheetActivityUpdateModel: TimesheetActivityUpdateModel = {
      entryId: timesheetActivity.id,
      ...timesheetActivity,
      date: this.getDate(new Date(timesheetActivity.date)),
      dayShiftHours: this.parseHoursToTimeFormat(
        timesheetActivity.dayShiftHours,
      ),
      overtimeHours: this.parseHoursToTimeFormat(
        timesheetActivity.overtimeHours,
      ),
      nightShiftHours: this.parseHoursToTimeFormat(
        timesheetActivity.nightShiftHours,
      ),
      doubleShiftHours: this.parseHoursToTimeFormat(
        timesheetActivity.doubleShiftHours,
      ),
    };

    return firstValueFrom(
      this.httpService.put(
        `${environment.webApiUrl}/api/TimeManagement/inspection/${inspectionId}/activity`,
        timesheetActivityUpdateModel,
        {
          withCredentials: true,
        },
      ),
    );
  }
}
